import { useAuthStore } from "@/store/authSlice";

/** Label tampilan untuk kode role internal. */
export const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Admin",
  UPLOADER: "Uploader",
};

/** Nama tampilan ramah untuk sebuah kode role. */
export function roleLabel(name: string): string {
  return ROLE_LABELS[name] ?? name;
}

/**
 * useRBAC — Role-based access control hook.
 *
 * Provides boolean flags for common document actions based on the
 * current user's role(s), matching the spec Section 6.2 permission matrix.
 *
 * Usage:
 *   const { canEdit, canApprove } = useRBAC();
 */
export function useRBAC() {
  const user = useAuthStore((s) => s.user);
  const hasRole = useAuthStore((s) => s.hasRole);

  const roles = user?.roles ?? [];

  // ── Permission helpers ────────────────────────────────────────────────────

  const isAdmin = roles.includes("ADMIN");
  const isAuditor = roles.includes("AUDITOR");
  const isLeader = roles.includes("LEADER");
  const isManager = roles.includes("MANAGER");
  const isTreasurer = roles.includes("TREASURER");
  const isUser = roles.includes("USER");
  const isGuest = roles.includes("GUEST");
  // Model baru: Uploader (boleh unggah/edit + AI extract).
  const isUploader = roles.includes("UPLOADER");

  // ── Document permissions ──────────────────────────────────────────────────

  /**
   * VIEW: All authenticated users (GUEST can only see PUBLIC docs).
   */
  const canView = isAdmin || isAuditor || isLeader || isManager || isTreasurer || isUser || isGuest;

  /**
   * DOWNLOAD: All except GUEST.
   */
  const canDownload = isAdmin || isUploader || isAuditor || isLeader || isManager || isTreasurer || isUser;

  /**
   * CREATE (upload new document): ADMIN, MANAGER, TREASURER, USER.
   */
  const canCreate = isAdmin || isUploader || isManager || isTreasurer || isUser;

  /**
   * EDIT: ADMIN, MANAGER, TREASURER (and creators can always edit their own).
   */
  const canEdit = isAdmin || isUploader || isManager || isTreasurer;

  /**
   * DELETE: ADMIN only (soft delete).
   */
  const canDelete = isAdmin;

  /**
   * REVIEW (Level 1): ADMIN, MANAGER — review submitted documents.
   */
  const canReview = isAdmin || isManager;

  /**
   * APPROVE (Level 2): ADMIN, LEADER — approve reviewed documents.
   */
  const canApprove = isAdmin || isLeader;

  /**
   * PUBLISH: ADMIN, LEADER — publish approved documents.
   */
  const canPublish = isAdmin || isLeader;

  /**
   * MANAGE USERS: ADMIN only.
   */
  const canManageUsers = isAdmin;

  /**
   * VIEW AUDIT LOGS: ADMIN, AUDITOR.
   */
  const canViewAudit = isAdmin || isAuditor;

  /**
   * MANAGE MASTER DATA: ADMIN only.
   */
  const canManageMaster = isAdmin;

  /**
   * MANAGE TAGS: ADMIN, MANAGER.
   */
  const canManageTags = isAdmin || isManager;

  /**
   * VIEW DASHBOARD: All authenticated users except GUEST.
   */
  const canViewDashboard = isAdmin || isUploader || isAuditor || isLeader || isManager || isTreasurer || isUser;

  /**
   * VIEW FULL STATS (all documents): ADMIN, AUDITOR.
   */
  const canViewFullStats = isAdmin || isAuditor;

  /**
   * AI EXTRACT (generate konten via AI): ADMIN & UPLOADER.
   */
  const canAiExtract = isAdmin || isUploader;

  // ── Document-specific permission check ────────────────────────────────────

  /**
   * canEditDocument — checks if user can edit a specific document
   * (considering creator ownership).
   */
  function canEditDocument(createdByUserId: string): boolean {
    if (!user) return false;
    if (canEdit) return true;
    return user.user_id === createdByUserId;
  }

  function canDeleteDocument(createdByUserId: string): boolean {
    if (!user) return false;
    if (canDelete) return true;
    return user.user_id === createdByUserId && isAdmin;
  }

  return {
    // Role flags
    isAdmin,
    isUploader,
    isAuditor,
    isLeader,
    isManager,
    isTreasurer,
    isUser,
    isGuest,

    // Action permissions
    canView,
    canDownload,
    canCreate,
    canEdit,
    canDelete,
    canReview,
    canApprove,
    canPublish,
    canManageUsers,
    canViewAudit,
    canManageMaster,
    canManageTags,
    canViewDashboard,
    canViewFullStats,
    canAiExtract,

    // Document-specific
    canEditDocument,
    canDeleteDocument,

    // Generic role check
    hasRole,
  };
}
